/**
 * An IOFlow flow's input form: the shared transaction screen in SUBMIT mode
 * (docs/flow_forms.md). The form is the transaction model generated from the
 * flow's inputs — header fields, detail grids, item_change, validations and
 * lookups all run exactly as on any entry screen — and its one button runs
 * the flow instead of saving (`makeFlowFormRuntime`).
 */
import { useMemo, useRef } from 'react'
import type { ReactNode } from 'react'
import { TxnScreen, type TxnSubmitMode } from '@twasta/page-runtime/txn/screen'
import type { TransactionModel } from '@twasta/page-runtime/txn/types'
import { makeFlowFormRuntime, type FlowRunResult } from '@/livepreview/runtime/flowFormRuntime'

export default function FlowFormScreen({ project, objName, model, title, notice, onStarted }: {
  project: string
  /** The form's object name — the flow menu node's own name. */
  objName: string
  model: TransactionModel
  title?: string
  notice?: ReactNode
  /** The flow was started; IOFlow's execution id. */
  onStarted: (executionId: string) => void
}) {
  const runtime = useMemo(() => makeFlowFormRuntime(project, objName), [project, objName])
  // The screen memoizes on `submit`; keep it stable across the host's renders.
  const onStartedRef = useRef(onStarted)
  onStartedRef.current = onStarted
  const submit = useMemo<TxnSubmitMode>(() => ({
    label: 'Run flow',
    title,
    notice,
    onDone: (res) => onStartedRef.current((res as FlowRunResult).execution_id || res.txn_id),
  }), [title, notice])
  return (
    <TxnScreen
      project={project}
      objName={objName}
      model={model}
      runtime={runtime}
      gridLayoutMode="customize"
      canSaveLayout={false}
      submit={submit}
    />
  )
}
